import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

@Directive({
  selector: '[sch-filter-trigger]',
  standalone: true,
})
export class FilterTriggerDirective implements OnDestroy {
  @Input('sch-filter-trigger') overlayTemplate!: TemplateRef<any>;
  @Input() fallbackStrategy: 'vertical' | 'horizontal' = 'vertical';
  private overlayRef: OverlayRef | null = null;
  @Input() triggerOffset = 10;
  private _anchorTo: ElementRef<HTMLElement> | null = null;

  @Input()
  public set anchorTo(v: HTMLElement) {
    this._anchorTo = this.getElementRef(v);
  }
  private readonly elRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef,
  ) {}

  @HostListener('click')
  toggleOverlay() {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
      return;
    }

    this.overlayRef = this.overlay.create(this.getOverlayConfig());

    const overlayConfig = this.overlayRef.getConfig();
    const positionStrategy =
      overlayConfig.positionStrategy as FlexibleConnectedPositionStrategy;
    positionStrategy.withPositions(this.getPositionStrategy());
    this.overlayRef.backdropClick().subscribe(() => this.overlayRef?.detach());
    const portal = new TemplatePortal(this.overlayTemplate, this.vcr);
    this.overlayRef.attach(portal);
    this.cdRef.markForCheck();
  }

  private getOverlayConfig() {
    return new OverlayConfig({
      positionStrategy: this.getOverlayPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
  }

  private getOverlayPositionStrategy(): FlexibleConnectedPositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(this._anchorTo ?? this.elRef)
      .withLockedPosition()
      .withGrowAfterOpen();
  }

  private getPositionStrategy() {
    let positions: ConnectedPosition[] = [];

    if (this.fallbackStrategy === 'vertical') {
      positions = [
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: this.triggerOffset,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -this.triggerOffset,
        },
      ];
    } else {
      positions = [
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: this.triggerOffset,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'end',
          overlayY: 'top',
          offsetX: -this.triggerOffset,
        },
      ];
    }

    return positions;
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  getElementRef(el: any) {
    return new ElementRef<HTMLElement>(el);
  }
}
